import React from "react";

import { Button } from "../../../components/ui/button";
import { forcedFieldBorderClasses } from "../../../components/ui/force-state";
import { cn } from "../../../utils/cn";

export interface AddressInputProps {
    /** Current draft text in the field. */
    value: string;
    onChange: (value: string) => void;
    /** Commit the current draft (Enter key or the CTA). */
    onAdd: () => void;
    /** Placeholder + aria-label for the field. */
    placeholder: string;
    /** CTA label. */
    addLabel: string;
    /** Disable the CTA (e.g. draft doesn't validate). */
    disabled?: boolean;
    /** Sim-only cursor key placed on the text field. */
    "data-sim-cursor-key"?: string;
    /** Sim-only — force the field's hover (darker border) visual. */
    forceHover?: boolean;
    /** Sim-only — force the field's focus border visual. Wins over hover. */
    forceFocus?: boolean;
}

/**
 * AddressInput — a single-line text field plus an "add" CTA button. Enter in
 * the field, or clicking the CTA, commits the draft via `onAdd`.
 *
 * Generic Component: the caller owns the draft state, validation, and what
 * "add" does. Pure controlled View.
 */
export function AddressInput({
    value,
    onChange,
    onAdd,
    placeholder,
    addLabel,
    disabled = false,
    "data-sim-cursor-key": simCursorKey,
    forceHover = false,
    forceFocus = false
}: AddressInputProps): React.ReactElement {
    return (
        <div className="flex items-center gap-2">
            <input
                data-sim-cursor-key={simCursorKey}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        onAdd();
                    }
                }}
                placeholder={placeholder}
                aria-label={placeholder}
                className={cn(
                    "h-8 flex-1 min-w-0 border border-line-base bg-transparent px-2.5 outline-none font-numeric text-sm text-foreground-base placeholder:text-foreground-neutral",
                    forcedFieldBorderClasses(forceHover, forceFocus)
                )}
            />
            <Button
                variant="transparent"
                interaction="underline"
                size="sm"
                onClick={onAdd}
                disabled={disabled}
            >
                {addLabel}
            </Button>
        </div>
    );
}
